import React, {useEffect, useState} from 'react';
import {useParams, useLocation} from 'react-router-dom';
import {httpRequest} from '../services/httpService';
import {useForm} from '../hooks/useForm';
import ExamsTable from './visitForm/ExamsTable';
import MedicinesTable from './visitForm/MedicinesTable';
import TreatmentsTable from './visitForm/TreatmentsTable';
import VaccinationsTable from './visitForm/VaccinationsTable';
import PrescriptionsList from './visitForm/PrescriptionsList';
import FilesList from './visitForm/FilesList';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    IconButton,
    MenuItem,
    Slider,
    TextField,
    Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import LinkField from "../components/LinkField";
import DateTimeField from '../components/DateTimeField';
import {EmptyGuid} from '../utils/constants';
import Loader from '../components/Loader';

export default function VisitForm() {
    const {id} = useParams();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const petId = query.get('petId');
    const fetchUrl = petId ? `/api/visit/${id}?petId=${petId}` : `/api/visit/${id}`;
    const saveUrl = `/api/visit/`;

    const validate = (data) => {
        const errors = {};
        if (!data.time) errors.time = 'Fecha es requerida';
        if (!data.reason) errors.reason = 'Motivo es requerido';
        if (!data.clientID || data.clientID === EmptyGuid) errors.clientID = 'Cliente es requerido';
        if (!data.petID || data.petID === EmptyGuid) errors.petID = 'Mascota es requerida';
        return errors;
    };

    const afterLoad = async (loadedData) => {
        if (loadedData.exams) {
            loadedData.exams = await Promise.all(
                loadedData.exams.map(async (exam) => {
                    if (exam.filesPackageID) {
                        exam.filesPackage = await httpRequest(`/api/file/${exam.filesPackageID}`, {});
                    }
                    return exam;
                })
            );
        }

        if (loadedData.filesPackageID) {
            loadedData.filesPackage = await httpRequest(`/api/file/${loadedData.filesPackageID}`, {});
        }

        return loadedData;
    };

    const {
        data,
        isLoading,
        error,
        validationErrors,
        handleInputChange,
        handleSubmit,
        isNew,
        close,
    } = useForm({id, fetchUrl, saveUrl, validate, afterLoad});

    const [clients, setClients] = useState([]);
    const [pets, setPets] = useState([]);
    const [isLoadingClients, setIsLoadingClients] = useState(false);

    const searchClients = async (searchText) => {
        setIsLoadingClients(true);
        try {
            const response = await httpRequest('/api/client/list', { 
                params: { 
                    skip: 0, 
                    take: 10,
                    search: searchText
                }
            });
            setClients(response.data || []);
        } catch (error) {
            console.error('Error searching clients:', error);
        } finally {
            setIsLoadingClients(false);
        }
    };

    const handleClientChange = (event, newValue) => {
        handleInputChange({target: {name: 'clientID', value: newValue?.id || null}});
        setPets(newValue?.pets || []);
    };

    if (isLoading) {
        return <Loader />;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{mx: 'auto'}}>
            {/* Header */}
            <Box
                sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <Typography variant="h5">
                        {isNew ? 'Visita nueva' : 'Editar Visita'}
                    </Typography>
                    <Button type="submit" variant="contained" color="primary">
                        Guardar
                    </Button>
                </Box>
                <IconButton variant="text" onClick={close}>
                    <CloseIcon fontSize="large"/>
                </IconButton>
            </Box>
            <Card sx={{padding: 2}}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid size={{xs: 12, sm: 6}}>
                            {isNew && !petId ? (
                                <Autocomplete
                                    fullWidth
                                    options={clients}
                                    loading={isLoadingClients}
                                    getOptionLabel={(option) => option.fullName || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    onChange={handleClientChange}
                                    value={clients.find(client => client.id === data.clientID) || null}
                                    onInputChange={(event, newInputValue) => {
                                        if (newInputValue) {
                                            searchClients(newInputValue);
                                        } else {
                                            setClients([]);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            label="Cliente"
                                            error={!!validationErrors.clientID}
                                            helperText={validationErrors.clientID}
                                            
                                        />
                                    )}
                                />
                            ) : (
                                <LinkField
                                    title="Cliente"
                                    url={`/clients/${data.clientID}`}
                                    value={data.clientName}
                                />
                            )}
                        </Grid>
                        <Grid size={{xs: 12, sm: 6}}>
                            {isNew && !petId ? (
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    select
                                    label="Mascota"
                                    name="petID"
                                    value={data.petID !== EmptyGuid ? data.petID : ''}
                                    onChange={handleInputChange}
                                    error={!!validationErrors.petID}
                                    helperText={validationErrors.petID}
                                    //required
                                    //disabled={!data.clientID || pets.length === 0}
                                >
                                    {pets.map((pet) => (
                                        <MenuItem key={pet.id} value={pet.id}>
                                            {pet.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : (
                                <LinkField
                                    title="Mascota"
                                    url={`/pets/${data.petID}`}
                                    value={data.petName}
                                />
                            )}
                        </Grid>
                        <Grid size={{xs: 12, sm: 6}}>
                            <DateTimeField
                                label="Fecha"
                                name="time"
                                value={data.time}
                                onChange={handleInputChange}
                                error={!!validationErrors.time}
                                helperText={validationErrors.time}
                            />
                        </Grid>
                        <Grid size={{xs: 12, sm: 6}}>
                            <LinkField
                                title="Registrado por"
                                url={`/employees/${data.employeeID}`}
                                value={data.employeeName}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{padding: 2, mt: 2}}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid size={{xs: 12}}>
                            <TextField
                                variant="standard"
                                fullWidth
                                multiline
                                rows={2}
                                label="Motivo de Consulta"
                                name="reason"
                                value={data.reason || ''}
                                onChange={handleInputChange}
                                error={!!validationErrors.reason}
                                helperText={validationErrors.reason}
                            />
                        </Grid>
                        <Grid size={{xs: 12, sm: 6}}>
                            <Typography>Nivel de Dolor - {data.dolor || 0}</Typography>
                            <Slider
                                value={data.dolor || 0}
                                onChange={(e, value) =>
                                    handleInputChange({target: {name: 'dolor', value}})
                                }
                                min={0}
                                max={100}
                                color={data.dolor <= 25 ? "primary" : data.dolor < 75 ? "warning" : "error"}
                                valueLabelDisplay="auto"
                            />
                        </Grid>
                        <Grid size={{xs: 12, sm: 6}} sx={{display: 'flex', alignItems: 'center'}}>
                            {[
                                {value: 1, label: 'Embarazada'},
                                {value: 2, label: 'Lactante'}
                            ].map((spec) => (
                                <FormControlLabel
                                    key={spec.value}
                                    control={
                                        <Checkbox
                                            checked={!!(data.specifics & spec.value)}
                                            onChange={(e) => {
                                                const updatedValue = e.target.checked
                                                    ? data.specifics | spec.value
                                                    : data.specifics & ~spec.value;
                                                handleInputChange({
                                                    target: {name: 'specifics', value: updatedValue},
                                                });
                                            }}
                                        />
                                    }
                                    label={spec.label}
                                    sx={{mr: 2, userSelect: 'none'}}
                                />
                            ))}
                        </Grid>
                        <Grid size={{xs: 12}}>
                            <TextField
                                variant="standard"
                                fullWidth
                                multiline
                                rows={2}
                                label="Anamnesis y Descripción del Caso"
                                name="assumption"
                                value={data.assumption || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{xs: 12}}>
                            <TextField
                                variant="standard"
                                fullWidth
                                multiline
                                rows={2}
                                label="Diagnóstico"
                                name="diagnosis"
                                value={data.diagnosis || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{xs: 12}}>
                            <TextField
                                variant="standard"
                                fullWidth
                                multiline
                                rows={3}
                                label="Notas"
                                name="notes"
                                value={data.notes || ''}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{xs: 6, sm: 4, md: 2}}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="T.LL.C/seg"
                                name="capillaryRefill"
                                type="number"
                                value={data.capillaryRefill}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{xs: 6, sm: 4, md: 2}}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="F.C."
                                name="heartRate"
                                type="number"
                                value={data.heartRate}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{xs: 6, sm: 4, md: 2}}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="F.R."
                                name="respiratoryRate"
                                type="number"
                                value={data.respiratoryRate}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{xs: 6, sm: 4, md: 2}}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="T°"
                                name="temperature"
                                type="number"
                                value={data.temperature}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{xs: 6, sm: 4, md: 2}}>
                            <TextField
                                variant="standard"
                                fullWidth
                                label="% de DHT"
                                name="dht"
                                type="number"
                                value={data.dht}
                                onChange={handleInputChange}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <ExamsTable
                visitID={id}
                exams={data.exams || []}
                onExamsChange={(updatedExams) =>
                    handleInputChange({ target: { name: 'exams', value: updatedExams } })
                }
            />

            {/* Medicines Table */}
            <MedicinesTable
                medicines={data.medicines || []}
                onMedicinesChange={(updatedMedicines) =>
                    handleInputChange({ target: { name: 'medicines', value: updatedMedicines } })
                }
            />

            {/* Treatments Table */}
            <TreatmentsTable
                treatments={data.treatments || []}
                onTreatmentsChange={(updatedTreatments) =>
                    handleInputChange({ target: { name: 'treatments', value: updatedTreatments } })
                }
            />

            {/* Vaccinations Table */}
            <VaccinationsTable
                vaccinations={data.vaccinations || []}
                onVaccinationsChange={(updatedVaccinations) =>
                    handleInputChange({ target: { name: 'vaccinations', value: updatedVaccinations } })
                }
            />

            {/* Prescriptions List */}
            <PrescriptionsList
                prescriptions={data.prescriptions || []}
                onPrescriptionsChange={(updatedPrescriptions) =>
                    handleInputChange({ target: { name: 'prescriptions', value: updatedPrescriptions } })
                }
            />

            {/* Resultados y archivos */}
            <FilesList
                filesPackage={data.filesPackage} // Pass filesPackage object
                onFilesChange={(updatedFilesPackage) =>
                    handleInputChange({ target: { name: 'filesPackage', value: updatedFilesPackage } })
                }
                visitID={id}
            />
            <Button sx={{mt:2}} type="submit" variant="contained" color="primary">
                Guardar
            </Button>
        </Box>
    );
}

